/**
 * SmartMissile - Evasive enemy missile
 * Subtly adjusts trajectory to evade nearby explosions.
 * Introduced at wave 6+.
 */

import { Missile } from './Missile.js';

export class SmartMissile extends Missile {
  constructor(opts) {
    super({
      ...opts,
      color: opts.color || '#ffaa33',
      trailColor: opts.trailColor || '#cc7700',
    });

    this.type = 'smart';
    this.avoidStrength = 0.15; // How strongly it avoids explosions (fraction of speed)
    this.maxTurnRate = 0.3; // Max radians per second of turn
    this.currentAngle = Math.atan2(this.vy, this.vx);
  }

  update(dt, explosions) {
    if (!this.alive) return;

    // Avoidance logic: check nearby explosions
    if (explosions && explosions.length > 0) {
      this._avoidExplosions(dt, explosions);
    }

    // Normal movement
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    this.traveled += this.speed * dt;

    // Add to trail
    this.trail.push({ x: this.x, y: this.y });
    if (this.trail.length > 30) {
      this.trail.shift();
    }

    // Check if reached target
    if (this.traveled >= this.totalDist) {
      this.x = this.targetX;
      this.y = this.targetY;
      this.alive = false;
    }
  }

  _avoidExplosions(dt, explosions) {
    let avoidX = 0;
    let avoidY = 0;

    for (const exp of explosions) {
      if (!exp.alive || exp.radius <= 0) continue;

      const dx = this.x - exp.x;
      const dy = this.y - exp.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // Only avoid if within danger range (explosion radius + buffer)
      const dangerRadius = exp.maxRadius * 1.8;
      if (dist < dangerRadius && dist > 0) {
        // Push away from explosion center
        const force = (1 - dist / dangerRadius) * this.avoidStrength;
        avoidX += (dx / dist) * force * this.speed;
        avoidY += (dy / dist) * force * this.speed;
      }
    }

    // Apply avoidance with capped turn rate
    if (avoidX !== 0 || avoidY !== 0) {
      const newVx = this.vx + avoidX * dt * 3;
      const newVy = this.vy + avoidY * dt * 3;

      // Normalize to maintain speed
      const mag = Math.sqrt(newVx * newVx + newVy * newVy);
      if (mag > 0) {
        this.vx = (newVx / mag) * this.speed;
        this.vy = (newVy / mag) * this.speed;
      }

      // Ensure still heading generally downward (vy must stay positive)
      if (this.vy < this.speed * 0.2) {
        this.vy = this.speed * 0.2;
        const maxVx = Math.sqrt(this.speed * this.speed - this.vy * this.vy);
        this.vx = Math.sign(this.vx) * Math.min(Math.abs(this.vx), maxVx);
      }
    }
  }

  render(ctx) {
    if (!this.alive && this.trail.length === 0) return;

    // Draw trail with subtle glow
    if (this.trail.length > 1) {
      for (let i = 1; i < this.trail.length; i++) {
        const alpha = i / this.trail.length;
        ctx.strokeStyle = this.trailColor;
        ctx.globalAlpha = alpha * 0.6;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(this.trail[i - 1].x, this.trail[i - 1].y);
        ctx.lineTo(this.trail[i].x, this.trail[i].y);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    // Draw missile head with orange aura
    if (this.alive) {
      ctx.save();
      // Outer aura
      ctx.shadowColor = '#ffaa33';
      ctx.shadowBlur = 8;
      ctx.fillStyle = this.color;
      ctx.beginPath();
      ctx.arc(this.x, this.y, 2.5, 0, Math.PI * 2);
      ctx.fill();
      // Inner bright core
      ctx.fillStyle = '#ffdd88';
      ctx.beginPath();
      ctx.arc(this.x, this.y, 1, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }
}
